'use client';

import { mergeAttributes, Node } from '@tiptap/core';
import { type Editor, EditorContent, type NodeViewProps, NodeViewWrapper, ReactNodeViewRenderer } from '@tiptap/react';
import { Plus, X } from 'lucide-react';
import type { MouseEvent } from 'react';
import { MentionContext } from '@/app/components/shared/MentionsTextarea/context';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/app/components/ui/select';
import type {
  ClawSkillSource,
  ClawTemplateTokenFile,
  ClawTemplateTokenKind,
  ClawTemplateTokenOption,
  ClawUploadAcceptKind,
} from '@/app/types';
import { cn } from '@/app/utils';
import { parseTokenAccept, parseTokenFiles, parseTokenOptions } from './utils/claw-template-token-attrs';

const MAX_VISIBLE_ROWS = 8;
const DEFAULT_LINE_HEIGHT = 22;

interface ClawTemplateTokenExtensionOptions {
  onUploadSlotClick?: (slotId: string, accept?: ClawUploadAcceptKind[]) => void;
  onUploadSlotFileRemove?: (filePath: string) => void;
  /**
   * 关掉 skill 槽位（点击 skill chip 的 ×）时触发。
   * 业务侧据此清空整份输入草稿，而不仅是删掉这个 chip 节点。
   * 不提供时退化为仅 deleteNode。
   */
  onSkillTokenRemove?: () => void;
}

interface EditorAreaProps {
  editor: Editor | null;
  isEmpty: boolean;
  placeholderText: string;
}

interface ClawTemplateTokenAttrs {
  id?: string;
  kind?: ClawTemplateTokenKind;
  label?: string;
  value?: string;
  placeholder?: string;
  options?: string | ClawTemplateTokenOption[];
  files?: string | ClawTemplateTokenFile[];
  accept?: string | ClawUploadAcceptKind[];
  source?: ClawSkillSource;
}

const getStringAttr = (value: unknown): string => (typeof value === 'string' ? value : '');

const ClawTemplateTokenNodeView = ({ deleteNode, extension, node, updateAttributes }: NodeViewProps) => {
  const attrs = node.attrs as ClawTemplateTokenAttrs;
  const kind = attrs.kind ?? 'input';
  const id = getStringAttr(attrs.id);
  const label = getStringAttr(attrs.label);
  const value = getStringAttr(attrs.value);
  const placeholder = getStringAttr(attrs.placeholder) || label;
  const options = parseTokenOptions(attrs.options);
  const files = parseTokenFiles(attrs.files);
  const accept = parseTokenAccept(attrs.accept);
  const extensionOptions = extension.options as ClawTemplateTokenExtensionOptions;

  const stopEditorMouseDown = (event: MouseEvent) => {
    event.stopPropagation();
  };

  if (kind === 'skill') {
    return (
      <NodeViewWrapper as="span" className="relative mr-1 inline-flex align-middle" contentEditable={false}>
        <span className="inline-flex h-7 items-center rounded-12 bg-system-white08 light:bg-[rgba(18,24,38,0.06)] px-2 font-medium light:text-[rgba(18,24,38,0.72)] text-[#D1D5DB] text-sm leading-7">
          /{label}
        </span>
        <button
          type="button"
          aria-label="Remove template"
          className="absolute top-[-4px] right-[-4px] flex size-[14px] items-center justify-center rounded-full border border-white/35 light:border-black/15 bg-black/80 light:bg-white light:text-[#121826] text-white leading-none"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            // 关掉 skill 槽位视为放弃整份技能模板：交给业务侧清空全部输入；
            // 未注入回调时退化为只删本节点。
            if (extensionOptions.onSkillTokenRemove) {
              extensionOptions.onSkillTokenRemove();
            } else {
              deleteNode();
            }
          }}
        >
          <X className="size-2" />
        </button>
      </NodeViewWrapper>
    );
  }

  if (kind === 'upload') {
    const handleRemoveFile = (filePath: string) => {
      const nextFiles = files.filter((file) => file.filePath !== filePath);
      updateAttributes({ files: nextFiles.length ? JSON.stringify(nextFiles) : '' });
      extensionOptions.onUploadSlotFileRemove?.(filePath);
    };

    return (
      <NodeViewWrapper
        as="span"
        className="inline-flex items-center gap-1 py-[10px] align-middle"
        contentEditable={false}
      >
        {files.map((file) => (
          <span key={file.filePath} className="group relative inline-flex size-8 shrink-0 items-start">
            <span className="relative h-full min-w-px flex-1 overflow-hidden rounded border border-white/20">
              {file.previewUrl ? (
                <img src={file.previewUrl} alt="" className="absolute inset-0 size-full object-cover" />
              ) : (
                <span className="flex size-full items-center justify-center bg-system-white08 text-[8px] text-white/60">
                  {file.fileName.slice(0, 1).toUpperCase()}
                </span>
              )}
            </span>
            <button
              type="button"
              aria-label="Remove uploaded file"
              className="absolute top-[-3px] right-[-3px] flex size-[14px] items-center justify-center rounded-full border border-white/35 bg-black/80 text-white leading-none opacity-0 transition-opacity group-hover:opacity-100"
              onMouseDown={(event) => {
                event.preventDefault();
                event.stopPropagation();
              }}
              onClick={() => handleRemoveFile(file.filePath)}
            >
              <X className="size-2" />
            </button>
          </span>
        ))}
        <button
          type="button"
          className="mx-1 inline-flex h-7 items-center gap-1 rounded-12 bg-system-white08 light:bg-[rgba(18,24,38,0.06)] px-2.5 font-medium light:text-[rgba(18,24,38,0.72)] text-[#D1D5DB] text-sm leading-7 transition-colors hover:bg-system-white12 light:hover:bg-[rgba(18,24,38,0.1)]"
          onMouseDown={(event) => {
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={() => extensionOptions.onUploadSlotClick?.(id, accept.length ? accept : undefined)}
        >
          <Plus className="size-3" />
          {value || label}
        </button>
      </NodeViewWrapper>
    );
  }

  if (kind === 'select') {
    // options 为空时 Radix Select 无可选项（且 SelectItem 不允许空 value），退化为纯文本展示。
    if (!options.length) {
      return (
        <NodeViewWrapper as="span" className="mx-1 inline-flex align-middle" contentEditable={false}>
          <span className="inline-flex h-7 items-center rounded-12 bg-system-white08 light:bg-[rgba(18,24,38,0.06)] px-3 font-medium light:text-[rgba(18,24,38,0.72)] text-sm text-white/80">
            {value || label}
          </span>
        </NodeViewWrapper>
      );
    }

    return (
      <NodeViewWrapper as="span" className="mx-1 inline-flex align-middle" contentEditable={false}>
        <Select value={value || undefined} onValueChange={(next) => updateAttributes({ value: next })}>
          <SelectTrigger
            className="h-7 w-auto gap-1 rounded-12 border-0 bg-system-white08 light:bg-[rgba(18,24,38,0.06)] px-3 font-medium light:text-[rgba(18,24,38,0.72)] text-sm text-white/80"
            onMouseDown={stopEditorMouseDown}
            onClick={(event) => event.stopPropagation()}
          >
            <SelectValue placeholder={label} />
          </SelectTrigger>
          <SelectContent>
            {options.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </NodeViewWrapper>
    );
  }

  return (
    <NodeViewWrapper as="span" className="mx-1 inline-flex max-w-full align-middle" contentEditable={false}>
      {/* inline-grid + 镜像 sizer：隐藏 span 随文本撑开网格轨道宽度，input 铺满该轨道，从而随内容自动变长；
          max-w-full 限制在编辑区内，超出时整个 token 作为原子节点换行而非横向溢出。 */}
      <span className="relative inline-grid max-w-full items-center">
        <span
          aria-hidden
          className="invisible col-start-1 row-start-1 h-7 min-w-64 whitespace-pre rounded-12 px-2.5 font-medium text-sm"
        >
          {value || placeholder || ''}
        </span>
        <input
          value={value}
          placeholder={placeholder}
          className={cn(
            'col-start-1 row-start-1 h-7 w-full rounded-12 bg-system-white08 light:bg-[rgba(18,24,38,0.06)] px-2.5 font-medium text-sm outline-none',
            value ? 'light:text-[#121826] text-white' : 'text-[#6D6D6D] placeholder:text-[#6D6D6D]',
          )}
          onMouseDown={stopEditorMouseDown}
          onClick={(event) => event.stopPropagation()}
          onChange={(event) => updateAttributes({ value: event.target.value })}
        />
      </span>
    </NodeViewWrapper>
  );
};

export const createClawTemplateTokenExtension = (options: ClawTemplateTokenExtensionOptions = {}) =>
  Node.create({
    name: 'claw-template-token',
    group: 'inline',
    inline: true,
    atom: true,
    selectable: true,

    addOptions() {
      return options;
    },

    addAttributes() {
      return {
        id: {
          default: null,
          parseHTML: (element) => element.getAttribute('data-id'),
          renderHTML: (attributes) => (attributes.id ? { 'data-id': attributes.id } : {}),
        },
        kind: {
          default: 'input',
          parseHTML: (element) => element.getAttribute('data-kind'),
          renderHTML: (attributes) => (attributes.kind ? { 'data-kind': attributes.kind } : {}),
        },
        label: {
          default: '',
          parseHTML: (element) => element.getAttribute('data-label') || element.textContent || '',
          renderHTML: (attributes) => (attributes.label ? { 'data-label': attributes.label } : {}),
        },
        value: {
          default: '',
          parseHTML: (element) => element.getAttribute('data-value') || '',
          renderHTML: (attributes) => (attributes.value ? { 'data-value': attributes.value } : {}),
        },
        placeholder: {
          default: '',
          parseHTML: (element) => element.getAttribute('data-placeholder') || '',
          renderHTML: (attributes) => (attributes.placeholder ? { 'data-placeholder': attributes.placeholder } : {}),
        },
        options: {
          default: '',
          parseHTML: (element) => element.getAttribute('data-options') || '',
          renderHTML: (attributes) =>
            attributes.options
              ? {
                  'data-options':
                    typeof attributes.options === 'string' ? attributes.options : JSON.stringify(attributes.options),
                }
              : {},
        },
        files: {
          default: '',
          parseHTML: (element) => element.getAttribute('data-files') || '',
          renderHTML: (attributes) =>
            attributes.files
              ? {
                  'data-files':
                    typeof attributes.files === 'string' ? attributes.files : JSON.stringify(attributes.files),
                }
              : {},
        },
        accept: {
          default: '',
          parseHTML: (element) => element.getAttribute('data-accept') || '',
          renderHTML: (attributes) =>
            attributes.accept
              ? {
                  'data-accept':
                    typeof attributes.accept === 'string' ? attributes.accept : JSON.stringify(attributes.accept),
                }
              : {},
        },
        source: {
          default: '',
          parseHTML: (element) => element.getAttribute('data-source') || '',
          renderHTML: (attributes) => (attributes.source ? { 'data-source': attributes.source } : {}),
        },
      };
    },

    parseHTML() {
      return [{ tag: 'span[data-type="claw-template-token"]' }];
    },

    renderHTML({ HTMLAttributes }) {
      const nodeText =
        HTMLAttributes.kind === 'skill'
          ? `/${HTMLAttributes.label ?? ''}`
          : (HTMLAttributes.value ?? HTMLAttributes.label ?? '');
      return ['span', mergeAttributes({ 'data-type': 'claw-template-token' }, HTMLAttributes), nodeText];
    },

    addNodeView() {
      return ReactNodeViewRenderer(ClawTemplateTokenNodeView);
    },
  });

export const EditorArea = ({ editor, isEmpty, placeholderText }: EditorAreaProps) => {
  return (
    <div
      className="relative my-[5px] flex flex-1 cursor-text items-start"
      style={{
        minHeight: `${DEFAULT_LINE_HEIGHT}px`,
        maxHeight: `${DEFAULT_LINE_HEIGHT * MAX_VISIBLE_ROWS}px`,
        overflowY: 'auto',
      }}
      onMouseDown={() => {
        if (editor && !editor.isDestroyed) {
          editor.commands.focus();
        }
      }}
    >
      <MentionContext.Provider value={{}}>
        <EditorContent editor={editor} className="w-full" />
      </MentionContext.Provider>
      {isEmpty ? (
        <div className="pointer-events-none absolute inset-0 flex select-none items-center truncate text-sm text-system-white36">
          {placeholderText}
        </div>
      ) : null}
    </div>
  );
};
